import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Compass, BadgeCheck, RefreshCcw, ListChecks } from "lucide-react";
import { useRef } from "react";
import { ABOUT_IMG } from "@/lib/data";
import { FadeUp, MaskLine } from "@/components/Reveal";

const VALUES = [
    {
        icon: Compass,
        title: "Precision first",
        desc: "Surveyed, specified and documented before a single wire is pulled or pipe is cut.",
    },
    {
        icon: BadgeCheck,
        title: "One accountable partner",
        desc: "A single point of contact across all four trades — no chasing subcontractors.",
    },
    {
        icon: RefreshCcw,
        title: "End-to-end lifecycle",
        desc: "We install, commission, maintain and repair the systems we deliver.",
    },
    {
        icon: ListChecks,
        title: "Standards-led",
        desc: "Every job is executed against the relevant codes and safety requirements.",
    },
];

export default function About() {
    const bandRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: bandRef,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

    return (
        <>
            <section className="relative overflow-hidden pb-20 pt-40 md:pb-28 md:pt-48">
                <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(ellipse 55% 45% at 20% 15%, rgba(245,158,11,0.1), transparent 65%)",
                    }}
                />
                <div className="container-x relative">
                    <p className="eyebrow">About — Services &amp; Prestations Ltd.</p>
                    <h1 className="mt-6 font-display text-5xl font-extrabold tracking-tight text-white sm:text-6xl lg:text-7xl">
                        <MaskLine>Built on four trades.</MaskLine>
                        <MaskLine delay={0.15}>
                            <span className="text-amber-400">Driven by precision.</span>
                        </MaskLine>
                    </h1>
                    <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-2">
                        <FadeUp>
                            <p className="text-base leading-relaxed text-slate-300 md:text-lg">
                                Services &amp; Prestations Ltd. is a multi-discipline
                                technical services company. We plan, install, secure
                                and maintain the systems a building depends on — from
                                the distribution board to the boiler room, from the
                                camera at the gate to the washing machine in the
                                kitchen.
                            </p>
                        </FadeUp>
                        <FadeUp delay={0.1}>
                            <p className="text-base leading-relaxed text-slate-300 md:text-lg">
                                Rather than juggling separate contractors for every
                                problem, our clients work with one accountable partner
                                organised around four regulated trades — each with its
                                own methods, standards and code of practice, and each
                                backed by the same promise: do it properly, once.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <section ref={bandRef} className="relative h-[42vh] overflow-hidden border-y border-white/10 md:h-[55vh]">
                <motion.img
                    src={ABOUT_IMG}
                    alt="Technical services"
                    loading="lazy"
                    style={{ y }}
                    className="card-img h-[116%] w-full object-cover"
                />
                <div className="absolute inset-0 bg-[#0B0F17]/40" />
            </section>

            <section className="py-20 md:py-28">
                <div className="container-x">
                    <FadeUp>
                        <p className="eyebrow">What we stand for</p>
                        <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                            Working principles, not slogans.
                        </h2>
                    </FadeUp>
                    <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {VALUES.map((v, i) => (
                            <FadeUp key={v.title} delay={i * 0.07} className="h-full">
                                <div className="group flex h-full flex-col rounded-2xl border border-white/10 bg-panel/50 p-6 transition-all duration-300 hover:border-amber-400/40 hover:bg-panel">
                                    <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-amber-400 transition-colors duration-300 group-hover:border-amber-400/40">
                                        <v.icon size={19} />
                                    </span>
                                    <h3 className="mt-5 font-display text-lg font-semibold tracking-tight text-white">
                                        {v.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-400">
                                        {v.desc}
                                    </p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-t border-white/10 py-20 md:py-28">
                <div className="container-x flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
                    <FadeUp>
                        <h2 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                            The registry is open.
                        </h2>
                        <p className="mt-3 max-w-md text-slate-400">
                            See the four trades we practise — each under its official
                            NACE code.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <Link data-testid="about-services-link" to="/services" className="btn-primary">
                            Explore the trades
                            <ArrowUpRight size={16} />
                        </Link>
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
